import { DraftPreviewModel, EMPTY_DRAFT_PREVIEW } from "./draftPreviewTypes";

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;

const asString = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

const asNumber = (value: unknown): number | undefined =>
  typeof value === "number" ? value : undefined;

const asProductIds = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    const productId = asString(asRecord(entry)?.productId);
    return productId ? [productId] : [];
  });
};

const asPriceProductIds = (
  value: unknown,
): DraftPreviewModel["priceProductIds"] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    const record = asRecord(entry);
    const productId = asString(record?.productId);
    if (!productId) return [];
    return [{ productId, quantity: asNumber(record?.quantity) ?? 1 }];
  });
};

const asContents = (value: unknown): DraftPreviewModel["contents"] => {
  if (!Array.isArray(value)) return [];
  return value.map((entry) => {
    const record = asRecord(entry);
    return {
      label: asString(record?.label),
      postContentFormatType: asString(record?.postContentFormatType),
      postTypeId: asString(record?.postTypeId),
      date: asString(record?.date),
      script: asString(record?.script),
    };
  });
};

const asMoney = (value: unknown): DraftPreviewMoneyOrNull => {
  const record = asRecord(value);
  const amount = asString(record?.amount);
  const currency = asString(record?.currency);
  return amount && currency ? { amount, currency } : null;
};

type DraftPreviewMoneyOrNull = DraftPreviewModel["fixedBudget"];

/**
 * Reads the wizard's draft JSON just enough to render a preview. Every field is
 * optional here even though the wizard's yup schema requires most of them, because
 * the agent may have only patched a handful of steps so far — a malformed or empty
 * `draftData` degrades to `EMPTY_DRAFT_PREVIEW` rather than throwing.
 */
export const parseDraftPreview = (draftData: string): DraftPreviewModel => {
  let raw: unknown;
  try {
    raw = JSON.parse(draftData);
  } catch {
    return EMPTY_DRAFT_PREVIEW;
  }
  const data = asRecord(raw);
  if (!data) return EMPTY_DRAFT_PREVIEW;

  const stepOne = asRecord(data.stepOne);
  const stepTwo = asRecord(data.stepTwo);
  const stepThree = asRecord(data.stepThree);
  const stepFour = asRecord(data.stepFour);
  const stepFive = asRecord(data.stepFive);

  return {
    name: asString(stepOne?.name) ?? "",
    description: asString(stepOne?.description) ?? "",
    object: asString(stepTwo?.object) ?? null,
    productIds: asProductIds(stepTwo?.products),
    contents: asContents(stepThree?.contents),
    priceProduct: asString(stepFour?.priceProduct) ?? null,
    priceProductIds: asPriceProductIds(stepFour?.priceProductIds),
    giftCardPrice: asMoney(asRecord(stepFour?.giftCard)?.price),
    budgetType: asString(stepFour?.budgetType) ?? null,
    fixedBudget: asMoney(stepFour?.fixedBudget),
    rightsOfUse: asString(stepFive?.rightsOfUse) ?? null,
    postInProfileTime: asString(stepFive?.postInProfileTime) ?? null,
  };
};
